// ─────────────────────────────────────────────────────────────────────────────
// demoStore.js — seed + your edits, and the only place demo state is written.
//
// WHAT LIVES WHERE
//   localStorage  small JSON: field overrides, settings, photo mode. Cheap to
//                 read synchronously during render.
//   IndexedDB     photo blobs. localStorage caps at ~5 MB and 30 jobs × ~6 photo
//                 slots would blow that on the third upload, so anything binary
//                 goes to IDB and localStorage keeps only an "idb:<id>" pointer.
//
// SYNC RENDER, ASYNC STORAGE
// React renders synchronously but IndexedDB is async, so initDemoStore() runs
// once at startup, reads every blob out of IDB and turns it into an object URL
// held in memory. resolvePhoto() is then a plain synchronous map lookup, which
// is what the page components need.
//
// READ-ONLY DEMO
// Nothing the demo *pages* do writes here. The pages are read-only by design —
// their Save buttons are intercepted and no-oped. This store is written only by
// the edit drawer, which is yours, not part of the demo surface.
// ─────────────────────────────────────────────────────────────────────────────

import { buildSeed } from './demoSeed';
import { photoUrl } from './demoPhotoManifest';

const LS_KEY = 'nova_demo_v1';
const IDB_NAME = 'nova_demo_photos';
const IDB_STORE = 'photos';
const MAX_EDGE = 1200; // px — long edge after downscale
const JPEG_QUALITY = 0.72;

/** Every field the drawer treats as a photo slot, by record kind. */
export const PHOTO_SLOTS = {
  job: ['before_photos', 'after_photos', 'temp_voltage_photos', 'fsr_photo', 'customer_signature'],
  attendance: ['clock_in_image_url', 'clock_out_image_url'],
};

/** Slots holding an array of photos rather than a single one. */
export const ARRAY_SLOTS = new Set(['before_photos', 'after_photos', 'temp_voltage_photos']);

// ─── Persisted shape ─────────────────────────────────────────────────────────

const emptyState = () => ({
  version: 1,
  // 'prefill' seeds every slot from the real server photo library so the demo
  // looks complete out of the box. 'blank' empties them so you can fill each one
  // yourself. Either way an explicit override always wins.
  photoMode: 'prefill',
  settings: {
    editor: false, // edit drawer hidden by default — presentation-safe
    badge: true, // the small DEMO pill
  },
  overrides: {
    jobs: {},
    attendance: {},
    clients: {},
    mandates: {},
  },
});

let state = emptyState();
let seed = null;
let ready = false;

/** idb:<id> -> blob: object URL. Populated by initDemoStore(). */
const blobUrls = new Map();
/** idb:<id> -> data: URI. Kept only so exportJSON() can inline photos. */
const blobData = new Map();

const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch (_) { /* a bad listener must not break the rest */ } });

/** Subscribe to store changes. Returns an unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ─── localStorage ────────────────────────────────────────────────────────────

function loadLocal() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw);
    const base = emptyState();
    return {
      ...base,
      ...parsed,
      settings: { ...base.settings, ...(parsed.settings || {}) },
      overrides: { ...base.overrides, ...(parsed.overrides || {}) },
    };
  } catch (_) {
    return emptyState();
  }
}

function saveLocal() {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch (err) {
    // Quota is the realistic failure here. Photos are in IDB so this should not
    // happen, but if it does the user needs to know their edit did not persist
    // rather than discovering it mid-demo.
    console.error('[demo] could not persist demo state:', err);
  }
}

// ─── IndexedDB ───────────────────────────────────────────────────────────────

function openDb() {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) db.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbTx(mode, fn) {
  return openDb().then(
    (db) => new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, mode);
      const store = tx.objectStore(IDB_STORE);
      const out = fn(store);
      tx.oncomplete = () => resolve(out && out.result !== undefined ? out.result : out);
      tx.onerror = () => reject(tx.error);
    }),
  );
}

const idbPut = (key, value) => idbTx('readwrite', (s) => s.put(value, key));
const idbDelete = (key) => idbTx('readwrite', (s) => s.delete(key));
const idbClear = () => idbTx('readwrite', (s) => s.clear());

function idbAll() {
  return openDb().then(
    (db) => new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const out = {};
      const req = store.openCursor();
      req.onsuccess = () => {
        const cur = req.result;
        if (!cur) { resolve(out); return; }
        out[cur.key] = cur.value;
        cur.continue();
      };
      req.onerror = () => reject(req.error);
    }),
  );
}

// ─── Startup ─────────────────────────────────────────────────────────────────

/**
 * Load persisted state and hydrate every stored photo into an object URL.
 * Safe to call more than once; the second call is a no-op.
 */
export async function initDemoStore() {
  if (ready) return;
  state = loadLocal();
  seed = buildSeed();

  try {
    const all = await idbAll();
    Object.entries(all).forEach(([key, rec]) => {
      if (!rec || !rec.dataUrl) return;
      blobData.set(key, rec.dataUrl);
      // Object URLs render faster than long data: URIs and keep the DOM small.
      try {
        blobUrls.set(key, URL.createObjectURL(dataUrlToBlob(rec.dataUrl)));
      } catch (_) {
        blobUrls.set(key, rec.dataUrl);
      }
    });
  } catch (err) {
    // No IDB (private mode, locked-down browser) is survivable: the server photo
    // gallery still works, only your own uploads are lost.
    console.warn('[demo] photo storage unavailable, uploads disabled:', err && err.message);
  }

  ready = true;
  emit();
}

export const isReady = () => ready;

// ─── Photo helpers ───────────────────────────────────────────────────────────

function dataUrlToBlob(dataUrl) {
  const [head, body] = dataUrl.split(',');
  const mime = (head.match(/:(.*?);/) || [])[1] || 'image/jpeg';
  if (/;base64/i.test(head)) {
    const bin = atob(body);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) buf[i] = bin.charCodeAt(i);
    return new Blob([buf], { type: mime });
  }
  return new Blob([decodeURIComponent(body)], { type: mime });
}

/**
 * Downscale to MAX_EDGE and re-encode as JPEG. Accepts a File or a Blob.
 *
 * This is load-bearing, not a nicety. The uploads folders hold only full-size
 * originals — 0.8 to 4.7 MB each, no thumbnail variant, served with
 * Cache-Control: max-age=0. Rendering a grid of those as 80px thumbnails queues
 * hundreds of megabytes behind the browser's six-connections-per-host limit and
 * nothing ever appears. Downscaled to 1200px they land at ~120–200 KB.
 */
function downscale(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error('Not a readable image'));
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', JPEG_QUALITY));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

let photoSeq = Date.now();

/**
 * Store an uploaded File and return the "idb:<id>" token to put in a photo slot.
 * Throws if IndexedDB is unavailable — callers should surface that rather than
 * silently dropping the upload.
 */
export async function putPhoto(file) {
  const dataUrl = await downscale(file);
  photoSeq += 1;
  const key = `idb:${photoSeq}`;
  await idbPut(key, { dataUrl, name: file.name || 'photo.jpg', at: Date.now() });
  blobData.set(key, dataUrl);
  try {
    blobUrls.set(key, URL.createObjectURL(dataUrlToBlob(dataUrl)));
  } catch (_) {
    blobUrls.set(key, dataUrl);
  }
  return key;
}

// ─── Server-photo cache ──────────────────────────────────────────────────────
//
// A /uploads/... path is cheap to store but expensive to render, so the first
// time one is needed we fetch it once, downscale it and keep the small copy.
// resolvePhoto() then transparently serves the cached version. Two payoffs: the
// demo stops pulling multi-megabyte originals on every page, and once cached it
// runs with no network at all — which matters on conference wifi.

/** Deterministic cache key for a server path, so it is cached at most once. */
function cacheKeyFor(path) {
  let h = 2166136261;
  for (let i = 0; i < path.length; i += 1) {
    h ^= path.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `idb:srv:${(h >>> 0).toString(36)}`;
}

export const isCached = (path) => blobUrls.has(cacheKeyFor(path));

/**
 * Fetch, downscale and store one server photo. Returns true if it is now
 * cached. The uploads host sends Access-Control-Allow-Origin: *, so the canvas
 * does not get tainted and toDataURL works.
 */
export async function cacheServerPhoto(path) {
  if (!path || !path.startsWith('/uploads/')) return false;
  const key = cacheKeyFor(path);
  if (blobUrls.has(key)) return true;

  const res = await fetch(photoUrl(path), { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const dataUrl = await downscale(await res.blob());

  await idbPut(key, { dataUrl, name: path, at: Date.now(), src: path });
  blobData.set(key, dataUrl);
  try {
    blobUrls.set(key, URL.createObjectURL(dataUrlToBlob(dataUrl)));
  } catch (_) {
    blobUrls.set(key, dataUrl);
  }
  return true;
}

/** Every distinct server photo the current dataset references. */
export function referencedServerPhotos() {
  const { jobs, attendance } = getData();
  const out = new Set();
  const add = (v) => {
    (Array.isArray(v) ? v : [v]).forEach((one) => {
      if (typeof one === 'string' && one.startsWith('/uploads/')) out.add(one);
    });
  };
  jobs.forEach((j) => PHOTO_SLOTS.job.forEach((s) => add(j[s])));
  attendance.forEach((a) => PHOTO_SLOTS.attendance.forEach((s) => add(a[s])));
  return [...out];
}

/**
 * Warm the cache for the whole demo. Runs a small number of fetches at a time —
 * the browser allows six per host and saturating them makes every request slow
 * rather than making the batch fast.
 */
export async function cacheAllPhotos(onProgress, concurrency = 4) {
  const paths = referencedServerPhotos().filter((p) => !isCached(p));
  const total = paths.length;
  let done = 0;
  let failed = 0;
  if (!total) {
    if (onProgress) onProgress({ done: 0, total: 0, failed: 0 });
    return { done: 0, total: 0, failed: 0 };
  }

  let cursor = 0;
  const worker = async () => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= paths.length) return;
      try {
        await cacheServerPhoto(paths[i]);
      } catch (_) {
        failed += 1; // one unreachable file must not abort the batch
      }
      done += 1;
      if (onProgress) onProgress({ done, total, failed });
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, total) }, worker));
  emit();
  return { done, total, failed };
}

/** Drop every cached server photo, keeping your own uploads. */
export async function clearServerCache() {
  const keys = [...blobUrls.keys()].filter((k) => k.startsWith('idb:srv:'));
  await Promise.all(keys.map(async (k) => {
    const url = blobUrls.get(k);
    if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
    blobUrls.delete(k);
    blobData.delete(k);
    try { await idbDelete(k); } catch (_) { /* already gone */ }
  }));
  emit();
}

/** Drop a stored photo and revoke its object URL. */
export async function deletePhoto(key) {
  if (!key || !key.startsWith('idb:')) return;
  const url = blobUrls.get(key);
  if (url && url.startsWith('blob:')) URL.revokeObjectURL(url);
  blobUrls.delete(key);
  blobData.delete(key);
  try { await idbDelete(key); } catch (_) { /* already gone */ }
}

/**
 * Turn any stored photo value into something an <img src> accepts.
 * Handles idb: tokens, data:/blob: URIs, absolute URLs and /uploads/ paths.
 */
export function resolvePhoto(value) {
  if (!value || typeof value !== 'string') return '';
  if (value.startsWith('idb:')) return blobUrls.get(value) || '';
  // Prefer the downscaled local copy when we have one. Falls back to the remote
  // original so an uncached photo still renders — just slowly.
  if (value.startsWith('/uploads/')) {
    const cached = blobUrls.get(cacheKeyFor(value));
    if (cached) return cached;
  }
  return photoUrl(value);
}

/** Resolve a slot that may hold either a single value or an array. */
export function resolvePhotoList(value) {
  if (Array.isArray(value)) return value.map(resolvePhoto).filter(Boolean);
  const one = resolvePhoto(value);
  return one ? [one] : [];
}

/**
 * Return a copy of a record with every photo slot resolved to a renderable URL.
 *
 * The demo PAGES never call resolvePhoto themselves — they take whatever the API
 * hands them and build their own URLs — so resolution has to happen on the way
 * out of demoApi. That is what lets a cached blob: URL, or a locally uploaded
 * photo, actually reach the page. The drawer deliberately does NOT use this: it
 * needs the raw stored value to know what is set.
 */
export function withResolvedPhotos(record, kind) {
  const slots = PHOTO_SLOTS[kind] || [];
  let out = record;
  slots.forEach((slot) => {
    const value = record[slot];
    if (value == null) return;
    const next = ARRAY_SLOTS.has(slot)
      ? (Array.isArray(value) ? value.map(resolvePhoto).filter(Boolean) : [])
      : resolvePhoto(value) || null;
    if (next !== value) {
      if (out === record) out = { ...record };
      out[slot] = next;
    }
  });
  return out;
}

// ─── Merged data ─────────────────────────────────────────────────────────────

const isPhotoField = (field) =>
  PHOTO_SLOTS.job.includes(field) || PHOTO_SLOTS.attendance.includes(field);

/** Blank out seeded photos when photoMode is 'blank'. Overrides still win. */
function applyPhotoMode(record, kind) {
  if (state.photoMode !== 'blank') return record;
  const slots = PHOTO_SLOTS[kind] || [];
  const out = { ...record };
  slots.forEach((slot) => { out[slot] = ARRAY_SLOTS.has(slot) ? [] : null; });
  return out;
}

function mergeOne(record, kind, bucket, id) {
  const patch = (state.overrides[bucket] || {})[id];
  const base = applyPhotoMode(record, kind);
  return patch ? { ...base, ...patch } : base;
}

/**
 * The dataset the API layer serves: seed with your edits applied.
 * Cheap enough to call per request — 30 jobs and ~131 attendance rows.
 */
export function getData() {
  if (!seed) seed = buildSeed();

  const jobs = seed.jobs.map((j) => mergeOne(j, 'job', 'jobs', j.schedule_id));
  const attendance = seed.attendance.map((a) => mergeOne(a, 'attendance', 'attendance', a.id));
  const clients = seed.clients.map((c) => mergeOne(c, 'client', 'clients', c.client_id));
  const mandates = seed.mandates.map((m) => mergeOne(m, 'mandate', 'mandates', m.client_id));

  // Client-level renames must follow through onto the rows that carry a
  // denormalised copy, or Job History and Attendance would disagree with the
  // Mandate page about what a client is called.
  const nameById = {};
  clients.forEach((c) => { nameById[c.client_id] = c.client_name; });
  const applyName = (row) =>
    (nameById[row.client_id] && nameById[row.client_id] !== row.client_name
      ? { ...row, client_name: nameById[row.client_id] }
      : row);

  return {
    anchor: seed.anchor,
    teams: seed.teams,
    officers: seed.officers,
    clients,
    jobs: jobs.map(applyName),
    attendance: attendance.map(applyName),
    mandates: mandates.map(applyName),
  };
}

/** Unmerged seed, for the drawer's "revert this field" affordance. */
export function getSeedData() {
  if (!seed) seed = buildSeed();
  return seed;
}

// ─── Writes (drawer only) ────────────────────────────────────────────────────

/** Patch one record. bucket is 'jobs' | 'attendance' | 'clients' | 'mandates'. */
export function setOverride(bucket, id, patch) {
  const current = state.overrides[bucket] || {};
  state.overrides = {
    ...state.overrides,
    [bucket]: { ...current, [id]: { ...(current[id] || {}), ...patch } },
  };
  saveLocal();
  emit();
}

/** Drop one field's override so it falls back to the seed value. */
export function clearOverrideField(bucket, id, field) {
  const rec = (state.overrides[bucket] || {})[id];
  if (!rec || !(field in rec)) return;
  const next = { ...rec };
  delete next[field];
  const bucketNext = { ...(state.overrides[bucket] || {}) };
  if (Object.keys(next).length) bucketNext[id] = next;
  else delete bucketNext[id];
  state.overrides = { ...state.overrides, [bucket]: bucketNext };
  saveLocal();
  emit();
}

/**
 * Assign a photo to a slot. For array slots, `index` picks which position;
 * passing null as the value removes that entry.
 */
export function setPhotoSlot(bucket, id, slot, value, index = 0) {
  const rows = bucket === 'jobs' ? getData().jobs : getData().attendance;
  const idKey = bucket === 'jobs' ? 'schedule_id' : 'id';
  const row = rows.find((r) => String(r[idKey]) === String(id));
  if (!row) return;

  if (ARRAY_SLOTS.has(slot)) {
    const list = Array.isArray(row[slot]) ? [...row[slot]] : [];
    if (value === null) list.splice(index, 1);
    else if (index >= list.length) list.push(value);
    else list[index] = value;
    setOverride(bucket, id, { [slot]: list });
  } else {
    setOverride(bucket, id, { [slot]: value });
  }
}

/** Put one photo into the same slot on every job belonging to a client. */
export function applyPhotoToClient(clientId, slot, value) {
  const { jobs } = getData();
  jobs
    .filter((j) => String(j.client_id) === String(clientId))
    .forEach((j) => {
      if (ARRAY_SLOTS.has(slot)) {
        const list = Array.isArray(j[slot]) && j[slot].length ? [...j[slot]] : [null];
        list[0] = value;
        setOverride('jobs', j.schedule_id, { [slot]: list.filter(Boolean) });
      } else {
        setOverride('jobs', j.schedule_id, { [slot]: value });
      }
    });
}

/** 'prefill' (seeded from the real photo library) or 'blank' (fill them yourself). */
export function setPhotoMode(mode) {
  state.photoMode = mode === 'blank' ? 'blank' : 'prefill';
  saveLocal();
  emit();
}
export const getPhotoMode = () => state.photoMode;

/** Strip every photo override, returning all slots to whatever photoMode says. */
export function clearPhotoOverrides() {
  const strip = (bucket) => {
    const next = {};
    Object.entries(state.overrides[bucket] || {}).forEach(([id, patch]) => {
      const kept = {};
      Object.entries(patch).forEach(([k, v]) => { if (!isPhotoField(k)) kept[k] = v; });
      if (Object.keys(kept).length) next[id] = kept;
    });
    return next;
  };
  state.overrides = {
    ...state.overrides,
    jobs: strip('jobs'),
    attendance: strip('attendance'),
  };
  saveLocal();
  emit();
}

// ─── Settings ────────────────────────────────────────────────────────────────

export const getSettings = () => ({ ...state.settings });

export function setSetting(key, value) {
  state.settings = { ...state.settings, [key]: value };
  saveLocal();
  emit();
}

export const isEditorEnabled = () => Boolean(state.settings.editor);
export const isBadgeEnabled = () => state.settings.badge !== false;

// ─── Portability ─────────────────────────────────────────────────────────────

/**
 * Export everything as one JSON file, with uploaded photos inlined as data URIs
 * so it survives a browser wipe and moves to the demo laptop. Server-gallery
 * picks stay as /uploads/ paths and cost nothing.
 */
export function exportJSON() {
  const photos = {};
  const walk = (bucket) => {
    Object.values(state.overrides[bucket] || {}).forEach((patch) => {
      Object.values(patch).forEach((v) => {
        const vals = Array.isArray(v) ? v : [v];
        vals.forEach((one) => {
          if (typeof one === 'string' && one.startsWith('idb:') && blobData.has(one)) {
            photos[one] = blobData.get(one);
          }
        });
      });
    });
  };
  walk('jobs');
  walk('attendance');

  return JSON.stringify({
    kind: 'nova-demo-export',
    version: 1,
    exportedAt: new Date().toISOString(),
    photoMode: state.photoMode,
    settings: state.settings,
    overrides: state.overrides,
    photos,
  }, null, 2);
}

/** Restore an export. Replaces current state wholesale. */
export async function importJSON(text) {
  const parsed = JSON.parse(text);
  if (parsed.kind !== 'nova-demo-export') {
    throw new Error('Not a NOVA demo export file.');
  }

  await idbClear();
  blobUrls.forEach((url) => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
  blobUrls.clear();
  blobData.clear();

  const photos = parsed.photos || {};
  await Promise.all(Object.entries(photos).map(async ([key, dataUrl]) => {
    await idbPut(key, { dataUrl, name: 'imported.jpg', at: Date.now() });
    blobData.set(key, dataUrl);
    try {
      blobUrls.set(key, URL.createObjectURL(dataUrlToBlob(dataUrl)));
    } catch (_) {
      blobUrls.set(key, dataUrl);
    }
  }));

  const base = emptyState();
  state = {
    ...base,
    photoMode: parsed.photoMode || base.photoMode,
    settings: { ...base.settings, ...(parsed.settings || {}) },
    overrides: { ...base.overrides, ...(parsed.overrides || {}) },
  };
  saveLocal();
  emit();
}

/** Back to a clean seed — clears overrides, settings and stored photos. */
export async function resetAll() {
  try { await idbClear(); } catch (_) { /* nothing stored */ }
  blobUrls.forEach((url) => { if (url.startsWith('blob:')) URL.revokeObjectURL(url); });
  blobUrls.clear();
  blobData.clear();
  state = emptyState();
  seed = buildSeed();
  saveLocal();
  emit();
}

/** Rough byte count of stored photos, for the drawer's storage readout. */
export function photoStorageBytes() {
  let total = 0;
  blobData.forEach((d) => { total += Math.ceil((d.length * 3) / 4); });
  return total;
}

export const photoCount = () => blobData.size;
